import { supabase } from '@/integrations/supabase/client';
import {
  AgencyAdvisorMemberRow,
  AgencyClientRow,
  AgencyTripRow,
  AgencyTripsQuery,
  AgencyTripsSummary,
  TripDetailResult,
  AgencyDashboardOverviewResult,
  AgencyDashboardPeriod,
  AdvisorDetailResult,
  ClientDetailResult,
  ClientKey,
} from '@/integrations/supabase/rpc-types';
import {
  normalizeAdvisorDetail,
  normalizeClientDetail,
} from '@/lib/dashboard/shared/detail-payload';

/**
 * Servizio per la dashboard agenzia (tab Panoramica, Advisor Members, Clienti).
 * Le RPC fanno tutto il lavoro di aggregazione: il FE si limita a esporre i dati ricevuti.
 */
export class AgencyDashboardService {
  static async getOverview(
    period: AgencyDashboardPeriod = '30d'
  ): Promise<AgencyDashboardOverviewResult> {
    // RPC non ancora presente nei tipi generati (types.ts va rigenerato dopo il deploy
    // della migration) — stesso workaround già usato in useFullProfile.ts.
    const { data, error } = await supabase.rpc('get_agency_dashboard_overview' as any, {
      p_period: period,
    });

    if (error) {
      throw new Error(`Errore nel caricamento della panoramica: ${error.message}`);
    }

    return data as unknown as AgencyDashboardOverviewResult;
  }

  static async getAdvisorMembers(
    period: AgencyDashboardPeriod = '30d'
  ): Promise<AgencyAdvisorMemberRow[]> {
    const { data, error } = await supabase.rpc('get_agency_advisor_members' as any, {
      p_period: period,
    });

    if (error) {
      throw new Error(`Errore nel caricamento dei membri del team: ${error.message}`);
    }

    return (data ?? []) as unknown as AgencyAdvisorMemberRow[];
  }

  /**
   * Elenco clienti della tab Clienti.
   *
   * `designerId` e `search` viaggiano come NULL quando non valorizzati: la RPC tratta
   * NULL come "nessun filtro", mentre una stringa vuota sarebbe un pattern LIKE che non
   * corrisponde a nulla.
   */
  static async getClients(
    period: AgencyDashboardPeriod = '30d',
    designerId: string | null = null,
    search: string | null = null
  ): Promise<AgencyClientRow[]> {
    const { data, error } = await supabase.rpc('get_agency_clients' as any, {
      p_period: period,
      p_designer_id: designerId,
      p_search: search?.trim() ? search.trim() : null,
    });

    if (error) {
      throw new Error(`Errore nel caricamento dei clienti: ${error.message}`);
    }

    return (data ?? []) as unknown as AgencyClientRow[];
  }

  /**
   * Scheda del singolo cliente.
   *
   * Ritorna `null` quando la chiave non esiste o non e' visibile: la RPC non distingue i
   * due casi di proposito, cosi' non e' possibile sondare l'esistenza dei clienti altrui.
   */
  static async getClientDetail(
    clientKey: ClientKey,
    period: AgencyDashboardPeriod = '30d',
    periodOnly = false
  ): Promise<ClientDetailResult | null> {
    const { data, error } = await supabase.rpc('get_client_detail' as any, {
      p_client_key: clientKey,
      p_period: period,
      p_period_only: periodOnly,
    });

    if (error) {
      throw new Error(`Errore nel caricamento della scheda cliente: ${error.message}`);
    }

    return normalizeClientDetail(data);
  }

  /**
   * Scheda del singolo consulente.
   *
   * Come `getClientDetail`, `null` copre sia "non esiste" sia "non puoi vederlo": la RPC
   * non distingue i due casi, per non trasformare la scheda in un modo di scoprire chi
   * lavora nelle altre agenzie.
   */
  static async getAdvisorDetail(
    advisorId: string,
    period: AgencyDashboardPeriod = '30d',
    periodOnly = false
  ): Promise<AdvisorDetailResult | null> {
    const { data, error } = await supabase.rpc('get_advisor_detail' as any, {
      p_advisor_id: advisorId,
      p_period: period,
      p_period_only: periodOnly,
    });

    if (error) {
      throw new Error(`Errore nel caricamento della scheda consulente: ${error.message}`);
    }

    return normalizeAdvisorDetail(data);
  }

  /**
   * Una pagina della lista viaggi (o tutte le righe con `limit: null`, per l'export).
   * Ogni riga porta `total_count`, il totale della vista prima della paginazione.
   */
  static async getTrips({
    period,
    designerId = null,
    search,
    state = null,
    limit = 25,
    offset = 0,
  }: AgencyTripsQuery): Promise<AgencyTripRow[]> {
    const { data, error } = await supabase.rpc('get_agency_trips' as any, {
      p_period: period,
      p_designer_id: designerId,
      p_search: search?.trim() ? search.trim() : null,
      p_state: state,
      p_limit: limit,
      p_offset: offset,
    });

    if (error) {
      throw new Error(`Errore nel caricamento dei viaggi: ${error.message}`);
    }

    return (data ?? []) as unknown as AgencyTripRow[];
  }

  /** Contatori della tab Viaggi: stessi filtri della lista, categoria esclusa. */
  static async getTripsSummary({
    period,
    designerId = null,
    search,
  }: Pick<AgencyTripsQuery, 'period' | 'designerId' | 'search'>): Promise<AgencyTripsSummary> {
    const { data, error } = await supabase.rpc('get_agency_trips_summary' as any, {
      p_period: period,
      p_designer_id: designerId,
      p_search: search?.trim() ? search.trim() : null,
    });

    if (error) {
      throw new Error(`Errore nel caricamento dei contatori viaggi: ${error.message}`);
    }

    return data as unknown as AgencyTripsSummary;
  }

  /**
   * Scheda del singolo viaggio. `null` copre sia "non esiste" sia "non puoi vederlo".
   */
  static async getTripDetail(itineraryId: string): Promise<TripDetailResult | null> {
    const { data, error } = await supabase.rpc('get_trip_detail' as any, {
      p_itinerary_id: itineraryId,
    });

    if (error) {
      throw new Error(`Errore nel caricamento della scheda viaggio: ${error.message}`);
    }

    return (data ?? null) as unknown as TripDetailResult | null;
  }
}
